import React, { useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import FlagCircleOutlinedIcon from "@mui/icons-material/FlagCircleOutlined";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import {
  BreakdownModal,
  FilterBar,
  StatCardsRow,
  TrendChartCard,
  RankedBarList,
  DataTableCard,
  PaginatedDataTableCard,
  AvatarNameCell,
  ProgressCell,
  TagCell,
  KeyInsightBanner,
  TrendTableCard,
  DonutBreakdownCard,
  SearchableDataTableCard,
  PaginationBar,
  SpecialtyBreakdownCard,
} from "./BreakDownModal";
import { CHART_LABELS_10PT, formatRelativeTime } from "./designTokens";
import {
  fetchCodeChanges,
  fetchCoderPerformanceMetrics,
  fetchDiagnosisChangesTimeline,
  fetchFinancialImpactBySpeciality,
  fetchProcedureChangesTimeline,
  fetchSpecialityAuditAnalysis,
  fetchSpecialtyAuditOutcomes,
  fetchTopDrg,
} from "./redux/WorkflowDashboardSlicer";
import {
  toCodeChangesPayload,
  toCoderPerformanceMetricsPayload,
  toDiagnosisTimelinePayload,
  toFinancialImpactBySpecialityPayload,
  toInsightsPayload,
  toProcedureTimelinePayload,
  toSpecialityAuditAnalysisPayload,
  toSpecialityAuditPayload,
} from "./utils/mapDashboardFilters";
import { useDispatch, useSelector } from "react-redux";

const BRAND_BLUE = "#1565C0";

/* =============================================================================
 * 1) Coder Accuracy — matches "coder breakdown" screenshot
 * ========================================================================== */
export function CoderAccuracyBreakdown({ open, onClose, pinned, onTogglePin }) {
  const dispatch = useDispatch();
  const {
    filters,
    coderAccuracy,
    coderPerformanceMetrics,
    coderAccuracyLastUpdated,
    coderPerformanceMetricsLoading,
  } = useSelector((state) => state.workflowDashboard);
  const [sortBy, setSortBy] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("");

  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchCoderPerformanceMetrics(
        toCoderPerformanceMetricsPayload(filters, { sortBy, sortOrder }),
      ),
    );
  }, [open, filters, sortBy, sortOrder, dispatch]);

  const rows = (
    Array.isArray(coderPerformanceMetrics)
      ? coderPerformanceMetrics
      : coderPerformanceMetrics?.data || []
  ).map((r, i) => ({
    id: i,
    name:
      r.coder_identity && r.coder_identity !== "nan"
        ? r.coder_identity
        : "Unspecified",
    accuracy: parseFloat(r.accuracy_rate) || 0,
    cases: r.cases_audited ?? 0,
    impact: `$${Number(r.revenue_impact || 0).toLocaleString()}`,
  }));
  const handleSortChange = (columnKey, order) => {
    // add
    const API_SORT_FIELD = {
      // add
      name: "coder_identity",
      accuracy: "accuracy_rate",
      cases: "cases_audited",
      impact: "revenue_impact",
    };
    setSortBy(columnKey ? API_SORT_FIELD[columnKey] : "");
    setSortOrder(order);
  };

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title="Coder Accuracy"
      // subtitle="The comparison of the total number of episodes audited and the number of episodes with a status of Reviewed, No Change"
      lastUpdated={formatRelativeTime(coderAccuracyLastUpdated)}
      pinned={pinned}
      onTogglePin={onTogglePin}
    >
      <StatCardsRow
        stats={[
          {
            icon: SpeedIcon,
            iconBg: "#EFF6FF",
            iconColor: BRAND_BLUE,
            label: "ACCURACY RATE",
            value: coderAccuracy?.accuracy_rate ?? "—",
          },
          {
            icon: AssignmentOutlinedIcon,
            iconBg: "#F5F3FF",
            iconColor: "#7C3AED",
            label: "REVIEWED EPISODES",
            value: coderAccuracy?.reviewed_episodes ?? 0,
            subtext: "All reviewed episodes",
          },
          {
            icon: FactCheckOutlinedIcon,
            iconBg: "#FFF7ED",
            iconColor: "#EA580C",
            label: "REVIEW OUTCOMES",
            value: (
              <Box sx={{ display: "flex", gap: 1.5, mt: 0.25 }}>
                <Typography
                  sx={{ fontSize: 16, fontWeight: 700, color: "#16A34A" }}
                >
                  {coderAccuracy?.review_outcomes?.correct ?? 0}{" "}
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>
                    CORRECT
                  </Box>
                </Typography>
                <Typography
                  sx={{ fontSize: 16, fontWeight: 700, color: "#DC2626" }}
                >
                  {coderAccuracy?.review_outcomes?.incorrect ?? 0}{" "}
                  <Box component="span" sx={{ fontSize: 10, fontWeight: 700 }}>
                    INCORRECT
                  </Box>
                </Typography>
              </Box>
            ),
          },
        ]}
      />

      <DataTableCard
        title="Coder Performance Metrics"
        columns={[
          {
            key: "name",
            label: "Coder Identity",
            sortable: true,
            render: (r) => <AvatarNameCell name={r.name} />,
          },
          {
            key: "accuracy",
            label: "Accuracy %",
            sortable: true,
            render: (r) => (
              <ProgressCell
                value={r.accuracy}
                color={r.accuracy < 90 ? "#F59E0B" : "#10B981"}
              />
            ),
          },
          { key: "cases", label: "Cases Audited", sortable: true },
          {
            key: "impact",
            label: "Revenue Impact",
            sortable: true,
            render: (r) => (
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: "#16A34A" }}
              >
                {r.impact}
              </Typography>
            ),
          },
        ]}
        rows={rows}
        sortKey={
          // reverse-map API field back to internal column key so DataTableCard highlights the right header
          {
            coder_identity: "name",
            accuracy_rate: "accuracy",
            cases_audited: "cases",
            revenue_impact: "impact",
          }[sortBy] || ""
        }
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        loading={coderPerformanceMetricsLoading}
        pagination={
          !coderPerformanceMetricsLoading && rows.length === 0
            ? "No coder data available"
            : undefined
        }
      />

      {/* <KeyInsightBanner
        variant="info"
        title="How to improve"
        text="Focus on coders with accuracy below target. Review training opportunities and provide targeted education to improve coding quality."
      /> */}
    </BreakdownModal>
  );
}

/* =============================================================================
 * 2) Code Changes — matches "Code Changes" screenshot
 * ========================================================================== */
export function CodeChangesBreakdown({ open, onClose, pinned, onTogglePin }) {
  const dispatch = useDispatch();
  const {
    filters,
    codeChanges,
    codeChangesLoading,  
    diagnosisChangesTimeline,
    diagnosisChangesTimelineLoading,
    procedureChangesTimeline,
    procedureChangesTimelineLoading,
    codeChangesLastUpdated,
  } = useSelector((state) => state.workflowDashboard);

  const [diagnosisPeriod, setDiagnosisPeriod] = React.useState("Daily");
  const [procedurePeriod, setProcedurePeriod] = React.useState("Daily");

  // Sort state — one per table, all independent
  const [codeChangesSortBy, setCodeChangesSortBy] = React.useState("");
  const [codeChangesSortOrder, setCodeChangesSortOrder] = React.useState("");
  const [diagnosisSortBy, setDiagnosisSortBy] = React.useState("");
  const [diagnosisSortOrder, setDiagnosisSortOrder] = React.useState("");
  const [procedureSortBy, setProcedureSortBy] = React.useState("");
  const [procedureSortOrder, setProcedureSortOrder] = React.useState("");

  // Code Change Metrics — fetch on open, refetch on sort/filter change
  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchCodeChanges(
        toCodeChangesPayload(filters, { sortBy: codeChangesSortBy, sortOrder: codeChangesSortOrder }),
      ),
    );
  }, [open, filters, codeChangesSortBy, codeChangesSortOrder, dispatch]);

  // Diagnosis timeline
  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchDiagnosisChangesTimeline(
        toDiagnosisTimelinePayload(filters, diagnosisPeriod, {
          sortBy: diagnosisSortBy,
          sortOrder: diagnosisSortOrder,
        }),
      ),
    );
  }, [open, filters, diagnosisPeriod, diagnosisSortBy, diagnosisSortOrder, dispatch]);

  // Procedure timeline
  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchProcedureChangesTimeline(
        toProcedureTimelinePayload(filters, procedurePeriod, {
          sortBy: procedureSortBy,
          sortOrder: procedureSortOrder,
        }),
      ),
    );
  }, [open, filters, procedurePeriod, procedureSortBy, procedureSortOrder, dispatch]);

  const categoryColorMap = {
    "Principal Diagnosis": "#1565C0",
    "Additional Diagnoses": "#7C3AED",
    "Procedure Codes": "#F59E0B",
    Other: "#94A3B8",
  };

  const codeChangeRows = (codeChanges?.data || []).map((c, i) => ({
    id: i,
    category: c.change_category,
    count: c.no_of_changes,
    pct: c.percentage,
  }));

  const diagnosisRows = (diagnosisChangesTimeline?.data || []).map((r, i) => ({
    id: i,
    date: r.date,
    audited: r.diagnoses_audited,
    changes: r.diagnosis_changes,
    rate: r.change_rate,
  }));

  const procedureRows = (procedureChangesTimeline?.data || []).map((r, i) => ({
    id: i,
    date: r.date,
    audited: r.procedures_audited,
    changes: r.procedure_changes,
    rate: r.change_rate,
  }));

  // Reverse-map API field -> internal column key, one per table
  const CODE_CHANGES_API_FIELD = { category: "change_category", count: "no_of_changes", pct: "percentage" };
  const CODE_CHANGES_REVERSE = Object.fromEntries(Object.entries(CODE_CHANGES_API_FIELD).map(([k, v]) => [v, k]));

  const DIAGNOSIS_API_FIELD = { date: "date", audited: "diagnosis_audited", changes: "diagnosis_changes", rate: "change_rate" };
  const DIAGNOSIS_REVERSE = Object.fromEntries(Object.entries(DIAGNOSIS_API_FIELD).map(([k, v]) => [v, k]));

  const PROCEDURE_API_FIELD = { date: "date", audited: "procedures_audited", changes: "procedure_changes", rate: "change_rate" };
  const PROCEDURE_REVERSE = Object.fromEntries(Object.entries(PROCEDURE_API_FIELD).map(([k, v]) => [v, k]));

  const handleCodeChangesSortChange = (columnKey, order) => {
    setCodeChangesSortBy(columnKey ? CODE_CHANGES_API_FIELD[columnKey] : "");
    setCodeChangesSortOrder(order);
  };

  const handleDiagnosisSortChange = (columnKey, order) => {
    setDiagnosisSortBy(columnKey ? DIAGNOSIS_API_FIELD[columnKey] : "");
    setDiagnosisSortOrder(order);
  };

  const handleProcedureSortChange = (columnKey, order) => {
    setProcedureSortBy(columnKey ? PROCEDURE_API_FIELD[columnKey] : "");
    setProcedureSortOrder(order);
  };

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title="Code Changes"
      // subtitle="Monitor diagnosis code changes identified during audits."
      lastUpdated={formatRelativeTime(codeChangesLastUpdated)}
      pinned={pinned}
      onTogglePin={onTogglePin}
    >
       <StatCardsRow
        stats={[
          {
            icon: ChangeCircleOutlinedIcon,
            iconBg: "#ECFDF5",
            iconColor: "#16A34A",
            label: "PRINCIPAL DIAGNOSIS CHANGES",
            value: codeChanges?.principal_diagnosis_changes ?? 0,
          },
          {
            icon: PercentIcon,
            iconBg: "#F5F3FF",
            iconColor: "#7C3AED",
            label: "ADDITIONAL DIAGNOSIS CHANGES",
            value: codeChanges?.additional_diagnosis_changes ?? 0,
          },
          {
            icon: RuleOutlinedIcon,
            iconBg: "#FFF7ED",
            iconColor: "#EA580C",
            label: "PROCEDURE CODE CHANGES",
            value: codeChanges?.procedure_code_changes ?? 0,
          },
          {
            icon: FlagCircleOutlinedIcon,
            iconBg: "#EFF6FF",
            iconColor: BRAND_BLUE,
            label: "TOTAL CODE CHANGES",
            value: codeChanges?.total_code_changes ?? 0,
          },
        ]}
      />

      <DataTableCard
        title="Code Change Metrics"
        columns={[
          {
            key: "category",
            label: "Change Category",
            sortable: true,
            render: (r) => (
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: categoryColorMap[r.category] || "#334155" }}>
                {r.category}
              </Typography>
            ),
          },
          { key: "count", label: "Number of Changes", sortable: true },
          { key: "pct", label: "Percentage of Total", sortable: true },
        ]}
        rows={codeChangeRows}
        sortKey={CODE_CHANGES_REVERSE[codeChangesSortBy] || ""}
        sortOrder={codeChangesSortOrder}
        onSortChange={handleCodeChangesSortChange}
        loading={codeChangesLoading}
      />

      <TrendTableCard
        title="Procedure Changes Over Time"
        description="Procedure changes decreased by 3.2% compared to the previous audit period."
        columns={[
          { key: "date", label: "Date", sortable: true },
          { key: "audited", label: "Procedures Audited", sortable: true },
          { key: "changes", label: "Procedure Changes", sortable: true },
          {
            key: "rate",
            label: "Change Rate",
            sortable: true,
            render: (r) => <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#7C3AED" }}>{r.rate}</Typography>,
          },
        ]}
        rows={procedureRows}
        defaultPeriod={procedurePeriod}
        onPeriodChange={setProcedurePeriod}
        loading={procedureChangesTimelineLoading}
        sortKey={PROCEDURE_REVERSE[procedureSortBy] || ""}
        sortOrder={procedureSortOrder}
        onSortChange={handleProcedureSortChange}
      />

      <TrendTableCard
        title="Diagnosis Changes Over Time"
        description="Diagnosis changes increased by 6.3% compared to the previous audit period, remaining close to the organisational benchmark."
        columns={[
          { key: "date", label: "Date", sortable: true },
          { key: "audited", label: "Diagnoses Audited", sortable: true },
          { key: "changes", label: "Diagnosis Changes", sortable: true },
          {
            key: "rate",
            label: "Change Rate",
            sortable: true,
            render: (r) => <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#16A34A" }}>{r.rate}</Typography>,
          },
        ]}
        rows={diagnosisRows}
        defaultPeriod={diagnosisPeriod}
        onPeriodChange={setDiagnosisPeriod}
        loading={diagnosisChangesTimelineLoading}
        sortKey={DIAGNOSIS_REVERSE[diagnosisSortBy] || ""}
        sortOrder={diagnosisSortOrder}
        onSortChange={handleDiagnosisSortChange}
      />

      {/* <KeyInsightBanner
        variant="success"
        text="Diagnosis changes increased by 6.3% compared to the previous audit period. Incorrect coding and documentation updates account for the majority of diagnosis changes. Focus on documentation quality and coding validation to reduce recurring changes."
      /> */}
    </BreakdownModal>
  );
}

/* =============================================================================
 * 3) Top 10 DRG — matches "v2 of drg" screenshot
 * ========================================================================== */

export function TopDrgBreakdown({ open, onClose, pinned, onTogglePin }) {
  const dispatch = useDispatch();
  const { filters, topDrg, topDrgLastUpdated } = useSelector(
    (state) => state.workflowDashboard,
  );

  // useEffect(() => {
  //   if (!open) return;
  //   // topDrg is already populated by the Insights tab / pinned-tile fetches,
  //   // but this guards against opening the breakdown before either has run
  //   dispatch(fetchTopDrg(toInsightsPayload(filters)));
  // }, [filters, dispatch]);

  const formatCompact = (num) => {
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `$${Math.round(num / 1000)}K`;
    return `$${Math.round(num)}`;
  };

  const commonDrgs = (topDrg?.top10_most_common_drgs || []).map((d, i) => ({
    rank: i + 1,
    label: `DRG ${d.drg_code}`,
    value: d.value,
  }));

const revenueDrgs = (topDrg?.top10_drgs_impacting_revenue || []).map(
  (d, i) => ({
    rank: i + 1,
    label: `MS-DRG ${d.drg_code}`,
    value: d.value,
    displayValue: `$${Number(d.value).toLocaleString()}`,
  }),
);

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title="Top 10 DRG"
      // subtitle="Analyze DRGs with the highest coding changes identified during audits."
      lastUpdated={formatRelativeTime(topDrgLastUpdated)}
      pinned={pinned}
      onTogglePin={onTogglePin}
    >
      <StatCardsRow
        stats={[
          { label: "TOTAL DRG CHANGES", value: topDrg?.total_changes ?? 0 },
          { label: "DRG CHANGE RATE", value: topDrg?.avg_change_rate ?? "0%" },
        ]}
      />

      <RankedBarList
        title="Top 10 Most Common DRGs"
        description="Visual ranking of DRGs with the highest coding changes."
        items={commonDrgs}
        barColor={BRAND_BLUE}
      />

      <RankedBarList
        title="Top 10 DRGs Impacting Revenue"
        description="Rank DRGs based on highest confirmed revenue impact from coding changes."
        items={revenueDrgs}
        barColor={BRAND_BLUE}
      />
    </BreakdownModal>
  );
}

/* -----------------------------------------------------------------------
 * Per-specialty DRG datasets that back the "<Specialty> DRG Breakdown"
 * drill-down modal (opened from the Specialty Audit Analysis table).
 * -------------------------------------------------------------------- */
const SPECIALTY_DRG_SEED = {
  Cardiology: [
    {
      code: "470",
      desc: "Major Joint Replacement or Reattachment",
      episodes: 450,
      changes: 120,
      revenue: 845000,
      completion: 92,
    },
    {
      code: "291",
      desc: "Heart Failure & Shock w MCC",
      episodes: 385,
      changes: 94,
      revenue: 620400,
      completion: 88,
    },
    {
      code: "309",
      desc: "Cardiac Arrhythmia w MCC",
      episodes: 312,
      changes: 72,
      revenue: 415800,
      completion: 95,
    },
    {
      code: "280",
      desc: "Acute Myocardial Infarction, Discharged Alive",
      episodes: 245,
      changes: 61,
      revenue: 388200,
      completion: 84,
    },
    {
      code: "310",
      desc: "Cardiac Arrhythmia w CC",
      episodes: 210,
      changes: 48,
      revenue: 295500,
      completion: 91,
    },
  ],
  Neurology: [
    {
      code: "064",
      desc: "Intracranial Hemorrhage w MCC",
      episodes: 298,
      changes: 88,
      revenue: 512300,
      completion: 90,
    },
    {
      code: "065",
      desc: "Intracranial Hemorrhage w CC",
      episodes: 240,
      changes: 66,
      revenue: 398600,
      completion: 87,
    },
    {
      code: "070",
      desc: "Nonspecific CVA & Precerebral Occlusion w MCC",
      episodes: 205,
      changes: 52,
      revenue: 312400,
      completion: 93,
    },
  ],
  Orthopedics: [
    {
      code: "470",
      desc: "Major Joint Replacement or Reattachment",
      episodes: 388,
      changes: 101,
      revenue: 601200,
      completion: 89,
    },
    {
      code: "460",
      desc: "Spinal Fusion Except Cervical w MCC",
      episodes: 262,
      changes: 74,
      revenue: 458700,
      completion: 85,
    },
    {
      code: "487",
      desc: "Other Musculoskeletal Sys Procedures w CC",
      episodes: 190,
      changes: 41,
      revenue: 264500,
      completion: 92,
    },
  ],
  Oncology: [
    {
      code: "846",
      desc: "Chemotherapy w Acute Leukemia as Secondary Dx",
      episodes: 210,
      changes: 58,
      revenue: 344900,
      completion: 81,
    },
    {
      code: "838",
      desc: "Chemo w/o Acute Leukemia w MCC",
      episodes: 176,
      changes: 44,
      revenue: 268300,
      completion: 79,
    },
  ],
  Emergency: [
    {
      code: "871",
      desc: "Septicemia w/o MV >96 Hours w MCC",
      episodes: 340,
      changes: 96,
      revenue: 512800,
      completion: 74,
    },
    {
      code: "872",
      desc: "Septicemia w/o MV >96 Hours w/o MCC",
      episodes: 264,
      changes: 62,
      revenue: 356200,
      completion: 71,
    },
    {
      code: "066",
      desc: "Intracranial Hemorrhage w/o CC/MCC",
      episodes: 188,
      changes: 38,
      revenue: 241100,
      completion: 76,
    },
  ],
};

// Pad each specialty's real seed rows out with a few extra generated rows so
// pagination has something to page through, without inventing more real-looking data.
function buildSpecialtyDrgRows(specialty) {
  const seed = SPECIALTY_DRG_SEED[specialty] || [];
  const extra = Array.from({ length: 6 }, (_, i) => ({
    code: `${300 + i * 7}`,
    desc: `${specialty} Related Procedure Category ${i + 1}`,
    episodes: 180 - i * 14,
    changes: 40 - i * 3,
    revenue: 220000 - i * 18000,
    completion: 88 - i * 2,
  }));
  return [...seed, ...extra].map((r, i) => ({ ...r, rank: i + 1, id: i }));
}

function SpecialtyDrgBreakdownModal({ specialty, open, onClose }) {
  const rows = specialty ? buildSpecialtyDrgRows(specialty) : [];

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title={`${specialty || ""} DRG Breakdown`}
      subtitle="Review DRGs contributing to coding changes within this specialty."
      showPin={false}
      maxWidth={900}
    >
      <PaginatedDataTableCard
        columns={[
          { key: "rank", label: "Rank" },
          {
            key: "code",
            label: "DRG Code",
            render: (r) => (
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}
              >
                DRG {r.code}
              </Typography>
            ),
          },
          { key: "desc", label: "Description" },
          { key: "episodes", label: "Episodes", align: "right" },
          { key: "changes", label: "DRG Changes", align: "right" },
          {
            key: "revenue",
            label: "Revenue Impact",
            align: "right",
            render: (r) => (
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: BRAND_BLUE }}
              >
                ${r.revenue.toLocaleString()}
              </Typography>
            ),
          },
          {
            key: "completion",
            label: "Completion %",
            align: "right",
            render: (r) => `${r.completion}%`,
          },
        ]}
        rows={rows}
        itemLabel="DRGs"
      />
    </BreakdownModal>
  );
}

/* =============================================================================
 * 4) Specialty Audit Outcomes — matches "Specialty Audit Outcomes" screenshot
 * ========================================================================== */

const SPECIALTY_COLOR_PALETTE = [
  "#F97316",
  "#7C3AED",
  "#10B981",
  "#EF4444",
  "#3B82F6",
  "#22C55E",
  "#EC4899",
  "#1E293B",
  "#F43F5E",
  "#0EA5E9",
  "#A855F7",
  "#84CC16",
];

export function SpecialtyAuditBreakdown({
  open,
  onClose,
  pinned,
  onTogglePin,
}) {
  const dispatch = useDispatch();
  const {
    filters,
    specialtyAuditOutcomes,
    specialityAuditAnalysis,
    specialtyAuditOutcomesLoading,
    specialityAuditAnalysisLoading,
    specialtyAuditOutcomesLastUpdated,
  } = useSelector((state) => state.workflowDashboard);

  const [sortBy, setSortBy] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("asc");
  const [drillSpecialty, setDrillSpecialty] = React.useState(null);
  const [donutView, setDonutView] = React.useState("pie");
    const [donutSearchInput, setDonutSearchInput] = React.useState("");
  const [donutSearch, setDonutSearch] = React.useState(""); // debounced value actually sent to API
  const [analysisSearchInput, setAnalysisSearchInput] = React.useState("");
  const [analysisSearch, setAnalysisSearch] = React.useState("");
    useEffect(() => {
    const t = setTimeout(() => setDonutSearch(donutSearchInput), 400);
    return () => clearTimeout(t);
  }, [donutSearchInput]);
    useEffect(() => {
    const t = setTimeout(() => setAnalysisSearch(analysisSearchInput), 400);
    return () => clearTimeout(t);
  }, [analysisSearchInput]);
  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchSpecialtyAuditOutcomes(toSpecialityAuditPayload(filters, donutView, donutSearch)),
    );
  }, [open, filters, donutView, donutSearch, dispatch]); // add donutSearch to deps

  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchSpecialityAuditAnalysis(
        toSpecialityAuditAnalysisPayload(filters, { sortBy, sortOrder, search: analysisSearch }),
      ),
    );
  }, [open, filters, sortBy, sortOrder, analysisSearch, dispatch]);

  // stable color per specialty name, shared between the donut and the table's bullet dots
  const colorFor = (name, i) =>
    SPECIALTY_COLOR_PALETTE[i % SPECIALTY_COLOR_PALETTE.length];

  const rawSpecialtyData =
    donutView === "table"
      ? specialtyAuditOutcomes?.table || []
      : specialtyAuditOutcomes?.pie || [];

  const specialtySegments = rawSpecialtyData.map((p, i) => ({
    label: p.speciality,
    value: p.audited_episodes,
    color: colorFor(p.speciality, i),
  }));

  const totalAuditedEpisodes = specialityAuditAnalysis?.totals?.total_audited_episodes ?? 0;
    const API_SORT_FIELD = {
    specialty: "speciality",
    audited: "audited_episodes",
    rate: "drg_change_rate",
    changes: "drg_changes",
  };
  const REVERSE_SORT_FIELD = Object.fromEntries(
    Object.entries(API_SORT_FIELD).map(([k, v]) => [v, k]),
  );

  const handleSortChange = (columnKey, order) => {
    setSortBy(columnKey ? API_SORT_FIELD[columnKey] : "");
    setSortOrder(order);
  };

  const analysisRows = (specialityAuditAnalysis?.data || []).map((r, i) => ({
    id: i,
    specialty: r.speciality,
    color: colorFor(r.speciality, i),
    audited: r.audited_episodes,
    rate: r.drg_change_rate,
    changes: r.drg_changes,
  }));
  const totalAuditedFromTable = specialityAuditAnalysis?.totals?.total_audited_episodes ?? 0;
  const totalDrgChangesFromTable = specialityAuditAnalysis?.totals?.total_drg_changes ?? 0;

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title="Speciality Audit Outcomes"
      // subtitle="Performance analytics and distribution across clinical specialties."
      lastUpdated={formatRelativeTime(specialtyAuditOutcomesLastUpdated)}
      pinned={pinned}
      onTogglePin={onTogglePin}
      maxWidth={850}
    >
      <StatCardsRow
        stats={[
          {
            icon: StarOutlineIcon,
            iconBg: "#ECFDF5",
            iconColor: "#16A34A",
            label: "TOP AUDITED SPECIALTY",
            value: specialtyAuditOutcomes?.top_audited_speciality ?? "—",
          },
          {
            icon: PercentIcon,
            iconBg: "#F5F3FF",
            iconColor: "#7C3AED",
            label: "PERCENTAGE OF TOTAL",
            value: specialtyAuditOutcomes?.percentage_of_total ?? "0%",
          },
          {
            icon: TaskAltOutlinedIcon,
            iconBg: "#FFF7ED",
            iconColor: "#EA580C",
            label: "COMPLETED AUDITS",
            value: specialtyAuditOutcomes?.completed_audits ?? 0,
          },
        ]}
      />

      <SpecialtyBreakdownCard
        title="Breakdown By Specialty"
        description="Total audited episodes by clinical specialty"
        segments={specialtySegments}
        centerLabel={{
          value: totalAuditedEpisodes.toLocaleString(),
          label: "TOTAL AUDITED\nEPISODES",
        }}
        footerLeft={`${rawSpecialtyData.length} specialties`}
        footerRight={`${totalAuditedEpisodes.toLocaleString()} total episodes`}
        view={donutView}
        onViewChange={setDonutView}
        loading={specialtyAuditOutcomesLoading}
        query={donutSearchInput}
        onQueryChange={setDonutSearchInput}
      />

      <SearchableDataTableCard
        title="Specialty Audit Analysis"
        description={
          specialityAuditAnalysisLoading
            ? "Loading…"
            : `${analysisRows.length} specialt${analysisRows.length === 1 ? "y" : "ies"}`
        }
        searchKey="specialty"
        searchPlaceholder="Search specialty..."
        columns={[
          {
            key: "specialty",
            label: "Specialty",
            sortable: true,
            render: (r) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: r.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 12.5, color: "#334155" }}>
                  {r.specialty}
                </Typography>
              </Box>
            ),
          },
          {
            key: "audited",
            label: "Audited Episodes",
            sortable: true,
            align: "right",
          },
          {
            key: "rate",
            label: "DRG Change Rate",
            sortable: true,
            align: "right",
          },
          {
            key: "changes",
            label: "DRG Changes",
            sortable: true,
            align: "right",
            render: (r) => (
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: r.changes >= 40 ? "#DC2626" : "#334155",
                  textAlign: "right",
                  width: "100%",
                }}
              >
                {r.changes}
              </Typography>
            ),
          },
        ]}
        rows={analysisRows}
        sortKey={REVERSE_SORT_FIELD[sortBy] || ""}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        footerLeft={`${totalAuditedFromTable.toLocaleString()} total audited episodes`}
        footerRight={`${totalDrgChangesFromTable} total DRG changes`}
        loading={specialityAuditAnalysisLoading}
        query={analysisSearchInput}
        onQueryChange={setAnalysisSearchInput}
      />

      <SpecialtyDrgBreakdownModal
        specialty={drillSpecialty}
        open={!!drillSpecialty}
        onClose={() => setDrillSpecialty(null)}
      />
    </BreakdownModal>
  );
}

/* =============================================================================
 * 5) Financial Impact — matches "finan" screenshot
 * ========================================================================== */

export function FinancialImpactBreakdown({
  open,
  onClose,
  pinned,
  onTogglePin,
}) {
  const dispatch = useDispatch();
  const {
    filters,
    financialImpact,
    financialImpactBySpeciality,
    financialImpactBySpecialityLoading,
    financialImpactLastUpdated,
  } = useSelector((state) => state.workflowDashboard);

  const [page, setPage] = React.useState(1);
  const pageSize = 5;
  const [sortBy, setSortBy] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("");

    const API_SORT_FIELD = {
    specialty: "speciality",
    impact: "financial_impact",
    episodes: "episodes",
    revenuePerEpisode: "average_impact",
  };
  const REVERSE_SORT_FIELD = Object.fromEntries( 
    Object.entries(API_SORT_FIELD).map(([k, v]) => [v, k]),
  );

  const handleSortChange = (columnKey, order) => { 
    setSortBy(columnKey ? API_SORT_FIELD[columnKey] : "");
    setSortOrder(order);
    setPage(1); 
  };

  useEffect(() => {
    if (!open) return;
    dispatch(
      fetchFinancialImpactBySpeciality(
        toFinancialImpactBySpecialityPayload(filters, { page, pageSize,sortBy, sortOrder }),
      ),
    );
  }, [open, filters, page,sortBy, sortOrder, dispatch]);

  // reset to page 1 whenever filters change while the modal is open
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const rows = (financialImpactBySpeciality?.data || []).map((r, i) => ({
    id: i,
    specialty: r.speciality,
    impact: `$${Number(r.financial_impact || 0).toLocaleString()}`,
    episodes: r.episodes,
    revenuePerEpisode: `$${Number(r.average_impact || 0).toLocaleString()}`,
  }));

  const totalRecords = financialImpactBySpeciality?.total_records ?? 0;
  const totalPages = financialImpactBySpeciality?.total_pages ?? 1;
  const start = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalRecords);

  return (
    <BreakdownModal
      open={open}
      onClose={onClose}
      title="Financial Impact"
      // subtitle="Analyze the financial value generated through coding improvements and audit outcomes."
      // showPin={false}
      lastUpdated={formatRelativeTime(financialImpactLastUpdated)}
      pinned={pinned}        
      onTogglePin={onTogglePin}
      // filters={
      //   <FilterBar
      //     filters={[
      //       { label: "Date Range", value: "", options: ["Last 30 days", "Last 90 days"] },
      //       { label: "Audit Status", value: "", options: ["Completed", "In Review"] },
      //       { label: "Specialty", value: "", options: ["Cardiology", "Neurology"] },
      //       { label: "Facility", value: "", options: ["Facility A", "Facility B"] },
      //       { label: "Auditor", value: "", options: ["All Auditors"] },
      //     ]}
      //     resetLabel="Reset Filters"
      //     applyLabel="Apply Filters"
      //     onReset={() => {}}
      //     onApply={() => {}}
      //   />
      // }
    >
      <StatCardsRow
        stats={[
          {
            label: "CONFIRMED FINANCIAL IMPACT",
            value: `$${financialImpact?.total_confirmed_revenue ?? 0}`,
          },
          {
            label: "AVG REVENUE PER EPISODE",
            value: `$${Number(financialImpact?.avg_revenue_per_episode || 0).toLocaleString()}`,
            subtext: "Calculated across all audited episodes",
          },
          {
            label: "HIGHEST REVENUE SPECIALTY",
            value: financialImpact?.highest_revenue_speciality ?? "—",
            subtext: financialImpact?.highest_revenue_speciality_value
              ? `$${financialImpact.highest_revenue_speciality_value}`
              : undefined,
            subtextColor: "#16A34A",
          },
        ]}
      />

      <DataTableCard
        title="Financial Impact Analysis"
        columns={[
          {
            key: "specialty",
            label: "Specialty",
            sortable: true,
            render: (r, i) => (
              <AvatarNameCell name={r.specialty} paletteIndex={i} />
            ),
          },
          {
            key: "impact",
            label: "Financial Impact",
            sortable: true,
            render: (r) => (
              <Typography
                sx={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}
              >
                {r.impact}
              </Typography>
            ),
          },
          { key: "episodes", label: "Episodes",sortable: true, },
          { key: "revenuePerEpisode", label: "Revenue Per Episode",sortable: true, },
        ]}
        rows={rows}
        sortKey={REVERSE_SORT_FIELD[sortBy] || ""} 
        sortOrder={sortOrder}
        onSortChange={handleSortChange} 
        loading={financialImpactBySpecialityLoading}
        footer={
          <PaginationBar
            page={page}
            pageCount={totalPages}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
            rangeLabel={
              financialImpactBySpecialityLoading
                ? "Loading…"
                : `${start}-${end} of ${totalRecords} specialties`
            }
            onPageChange={setPage}
          />
        }
      />
    </BreakdownModal>
  );
}

/* =============================================================================
 * BreakdownRouter — maps an insight id to its breakdown component.
 * Renders whichever breakdown is currently open (or nothing).
 * ========================================================================== */
const BREAKDOWNS = {
  "coder-accuracy": CoderAccuracyBreakdown,
  "diagnosis-changes": CodeChangesBreakdown,
  "top-drg-changes": TopDrgBreakdown,
  "specialty-audit-outcomes": SpecialtyAuditBreakdown,
  "confirmed-financial-impact": FinancialImpactBreakdown,
};

export function BreakdownRouter({ openId, onClose, pinnedIds, onTogglePin }) {
  if (!openId) return null;
  const Breakdown = BREAKDOWNS[openId];
  if (!Breakdown) return null;

  return (
    <Breakdown
      open={!!openId}
      onClose={onClose}
      pinned={pinnedIds.includes(openId)}
      onTogglePin={() => onTogglePin(openId)}
    />
  );
}
